import { useLanguageContext, LanguageContextType } from '../contexts/LanguageContext';
import { SupportedLanguage } from '../i18n';

export interface UseTranslationReturn {
  t: (key: string, params?: Record<string, any>) => string;
  currentLanguage: SupportedLanguage;
  changeLanguage: (lang: SupportedLanguage) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  getAvailableLanguages: () => SupportedLanguage[];
}

/**
 * Хук для работы с переводами
 * Предоставляет удобный интерфейс для доступа к системе переводов
 */
export const useTranslation = (): UseTranslationReturn => {
  const context = useLanguageContext();

  return {
    t: context.t,
    currentLanguage: context.currentLanguage,
    changeLanguage: context.changeLanguage,
    isLoading: context.isLoading,
    error: context.error,
    getAvailableLanguages: context.getAvailableLanguages
  };
};

/**
 * Хук для работы с переводами в конкретном namespace
 * Автоматически добавляет namespace к ключам переводов
 */
export const useNamespacedTranslation = (namespace: string): UseTranslationReturn => {
  const context = useLanguageContext();

  const namespacedT = (key: string, params?: Record<string, any>): string => {
    const fullKey = `${namespace}.${key}`;
    return context.t(fullKey, params);
  };

  return {
    t: namespacedT,
    currentLanguage: context.currentLanguage,
    changeLanguage: context.changeLanguage,
    isLoading: context.isLoading,
    error: context.error,
    getAvailableLanguages: context.getAvailableLanguages
  };
};

/**
 * Хук для получения только функции перевода
 * Для случаев, когда нужен только t()
 */
export const useT = (): ((key: string, params?: Record<string, any>) => string) => {
  const { t } = useTranslation();
  return t;
};

/**
 * Хук для получения только текущего языка
 */
export const useCurrentLanguage = (): SupportedLanguage => {
  const { currentLanguage } = useTranslation();
  return currentLanguage;
};

/**
 * Хук для проверки состояния загрузки переводов
 */
export const useTranslationLoading = (): boolean => {
  const { isLoading } = useTranslation();
  return isLoading;
}; 